import {
  createRouter,
  createWebHistory,
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteRecordRaw,
} from 'vue-router';

import { isAuthenticated } from '@/helpers/utils';
import { RouteNames, StoreMutations } from '@/helpers/enums';
import HomeLayout from '@/layouts/HomeLayout.vue';
import SubPageLayout from '@/layouts/SubPageLayout.vue';
import { i18n } from '@/i18n';
import store from '@/store';

const redirectAuthenticated = (
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): void => {
  if (isAuthenticated()) {
    next({ name: RouteNames.orders });
    return;
  }

  next();
};

const validateAuthenticated = (
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): void => {
  if (isAuthenticated()) {
    next();
    return;
  }

  next({ name: RouteNames.registrationWelcome });
};

const routes: Array<RouteRecordRaw> = [
  {
    // This needs to be/stay url with magic_link
    path: '/magic_link/:magicLinkToken',
    name: RouteNames.login,
    props: true,
    component: () => import('@/views/LoginView.vue'),
    beforeEnter: redirectAuthenticated,
  },
  {
    path: '/',
    name: 'main',
    component: HomeLayout,
    redirect: { name: RouteNames.orders },
    beforeEnter: validateAuthenticated,
    children: [
      {
        name: RouteNames.home,
        path: 'home',
        component: () => import('@/views/HomeView.vue'),
      },
      {
        path: 'home/driver-list',
        name: RouteNames.driverList,
        component: () => import('@/views/DriverListView.vue'),
      },
      {
        path: 'home/future-orders',
        name: RouteNames.futureOrders,
        component: () => import('@/views/FutureOrdersView.vue'),
      },
      {
        path: 'home/driver-list/:facilityOrderId',
        name: RouteNames.driverListShift,
        props: true,
        component: () => import('@/views/DriverListView.vue'),
      },
      {
        path: 'orders',
        name: RouteNames.orders,
        component: () => import('@/views/OrdersView.vue'),
      },
      {
        path: 'history',
        name: RouteNames.history,
        component: () => import('@/views/OrdersHistoryView.vue'),
      },
      {
        path: 'support',
        props: true,
        name: RouteNames.support,
        component: () => import('@/views/SupportView.vue'),
      },
    ],
  },
  {
    path: '/order',
    name: 'order',
    component: SubPageLayout,
    redirect: { name: RouteNames.orderDetails },
    beforeEnter: validateAuthenticated,
    children: [
      {
        path: ':facilityOrderId',
        name: RouteNames.orderDetails,
        props: true,
        component: () => import('@/views/OrderDetailsView.vue'),
      },
      {
        path: ':facilityOrderId/pack',
        name: RouteNames.orderPacking,
        props: true,
        component: () => import('@/views/OrderPackingView.vue'),
      },
      {
        path: ':facilityOrderId/pack/:facilityOrderBagId',
        name: RouteNames.orderPackingOneBag,
        props: true,
        component: () => import('@/views/OrderPackingView.vue'),
      },
      {
        path: ':facilityOrderId/items',
        name: RouteNames.orderItemsDetails,
        props: true,
        component: () => import('@/views/OrderItemsView.vue'),
      },
      {
        path: ':facilityOrderId/received-bags-review',
        name: RouteNames.orderReceivedBagsReview,
        props: true,
        component: () => import('@/views/ReceivedPackedBagsView.vue'),
      },
      {
        path: ':facilityOrderId/tag-packages',
        name: RouteNames.orderTagPackages,
        props: true,
        component: () => import('@/views/OrderTagPackagesView.vue'),
      },
    ],
  },
  {
    path: '/bag',
    name: 'bag',
    component: SubPageLayout,
    redirect: { name: RouteNames.bagDetails },
    beforeEnter: validateAuthenticated,
    children: [
      {
        path: ':facilityBagId',
        name: RouteNames.bagDetails,
        props: true,
        component: () => import('@/views/BagView.vue'),
      },
      {
        path: ':facilityBagId/items',
        name: RouteNames.bagItemsDetails,
        props: true,
        component: () => import('@/views/BagItemsView.vue'),
      },
      {
        path: ':facilityBagId/packages',
        name: RouteNames.bagPackagesDetails,
        props: true,
        component: () => import('@/views/BagPackagesView.vue'),
      },
      {
        path: ':facilityBagId/itemise/:serviceShortCode?',
        name: RouteNames.bagItemisation,
        component: () => import('@/views/BagItemiseView.vue'),
        props: true,
        meta: {
          title: i18n.global.t('bag.itemisation.title'),
        },
      },
      {
        path: ':facilityBagId/processing-time',
        name: RouteNames.itemisationProcessingTime,
        component: () => import('@/views/BagItemiseProcessingTime.vue'),
        props: true,
        meta: {
          title: i18n.global.t('bag.itemisation.processing_time.title'),
        },
      },
      {
        path: ':facilityBagId/itemise/review',
        name: RouteNames.bagItemisationReview,
        component: () => import('@/views/BagItemisationReviewView.vue'),
        props: true,
        meta: {
          title: i18n.global.t('bag.itemisation.review.title'),
        },
      },
      {
        path: ':facilityBagId/add-problem/:problemId',
        name: RouteNames.addBagProblem,
        component: () => import('@/views/BagAddProblem.vue'),
        props: true,
        meta: {
          title: RouteNames.addBagProblem,
        },
      },
    ],
  },
  {
    path: '/registration',
    name: RouteNames.registration,
    component: SubPageLayout,
    redirect: { name: RouteNames.registrationWelcome },
    beforeEnter: redirectAuthenticated,
    children: [
      {
        path: 'welcome',
        name: RouteNames.registrationWelcome,
        component: () => import('@/views/registration/WelcomeView.vue'),
        props: true,
        meta: {
          title: RouteNames.registrationWelcome,
        },
      },
      {
        path: 'register',
        name: RouteNames.registrationData,
        component: () => import('@/views/registration/RegistrationView.vue'),
        meta: {
          title: RouteNames.registrationData,
        },
      },
    ],
  },
  {
    path: '/support-data',
    name: RouteNames.supportData,
    component: SubPageLayout,
    redirect: { name: RouteNames.supportOrderProblemList },
    beforeEnter: validateAuthenticated,
    children: [
      {
        path: ':facilityOrderId',
        props: true,
        name: RouteNames.supportOrderProblemList,
        component: () => import('@/views/SupportOrderProblemListView.vue'),
      },
      {
        path: ':facilityOrderId/:facilityOrderProblemId',
        props: true,
        name: RouteNames.supportOrderProblemListPreselect,
        component: () => import('@/views/SupportOrderProblemListView.vue'),
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach(async (): Promise<void> => {
  // Store the previous path for back button as webView is not storing history
  store.commit(StoreMutations.resetPreviousRoute);
  document.body.classList.remove('modal--open');
});

export default router;
